// Scrubs credentials out of values on their way to a logging sink.

export const REDACTED = '[REDACTED]';
const FUNCTION_PLACEHOLDER = '[Function]';
const CIRCULAR = '[Circular]';
const TRUNCATED = '[Truncated]';
const UNREADABLE = '[Unreadable]';
const UNREDACTABLE = '[Unredactable]';
const MAX_DEPTH = 16;
// Counted per property, not per object: a cyclic graph has unboundedly many paths.
const MAX_EMITTED = 50000;

const SENSITIVE_FRAGMENTS = [
  'token',
  'password',
  'passwd',
  'secret',
  'apikey',
  'authorization',
  'credential',
  'jwt',
  'signature',
  // Exchanged for a JWT by POST /auth/login-saml.
  'samlrequestid',
  // Sent as the basic auth password by APIService.setApplicationCredentials().
  'applicationid',
  'sessionid',
  'csrf',
  'cookie',
];

// Too short to match as a fragment without swallowing `authorId`, `passenger`, `sigma`.
const SENSITIVE_EXACT = [
  'auth',
  'pwd',
  'pass',
  'sig',
];

// `key` is a credential only in a URL (`?key=<base64 user:pass>`); as an object key it is a translation key.
const SENSITIVE_EXACT_PARAMS = SENSITIVE_EXACT.concat(['key']);

const normalizeName = name => String(name).toLowerCase().replace(/[^a-z0-9]/g, '');

const isSensitiveName = (name, exactNames) => {
  const normalized = normalizeName(name);
  if (exactNames.indexOf(normalized) !== -1) {
    return true;
  }
  return SENSITIVE_FRAGMENTS.some(fragment => normalized.indexOf(fragment) !== -1);
};

export const isSensitiveKey = key => isSensitiveName(key, SENSITIVE_EXACT);

export const isSensitiveParam = name => isSensitiveName(name, SENSITIVE_EXACT_PARAMS);

// A leading separator and a bounded name, so prose and a base64 payload's `=` padding are not params.
const QUERY_PARAM = /([?&#])([A-Za-z0-9_.%\[\]-]{1,64})=([^&\s#?"'<>(),;\\]*)/g;

const JSON_SCALAR_MEMBER = /"([^"\\]*)"\s*:\s*("(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?|true|false|null)/g;
const JSON_CONTAINER_MEMBER = /"([^"\\]*)"\s*:\s*[{[]/g;
// One round of escaping only, and `\\\"` has to precede `\"` or the value ends at the first embedded quote.
const JSON_ESCAPED_MEMBER = /\\"([^"\\]*)\\"\s*:\s*\\"((?:\\\\\\"|[^"\\]|\\[^"])*)\\"/g;

// Values only, not whole query strings: the URL is the ApplicationCache key.
export const redactURL = value => value.replace(
  QUERY_PARAM,
  (param, separator, name) => (isSensitiveParam(name) ? `${separator}${name}=${REDACTED}` : param)
);

// Quoted spans are skipped so a brace inside a string value cannot unbalance the count.
const findSpanEnd = (value, openIndex) => {
  const open = value.charAt(openIndex);
  const close = open === '{' ? '}' : ']';
  let depth = 0;
  let inString = false;
  for (let index = openIndex; index < value.length; index++) {
    const character = value.charAt(index);
    if (inString) {
      if (character === '\\') {
        index++;
      } else if (character === '"') {
        inString = false;
      }
    } else if (character === '"') {
      inString = true;
    } else if (character === open) {
      depth++;
    } else if (character === close) {
      depth--;
      if (depth === 0) {
        return index;
      }
    }
  }
  return -1;
};

const redactJSONContainers = value => {
  let result = '';
  let copiedTo = 0;
  let match;
  JSON_CONTAINER_MEMBER.lastIndex = 0;
  while ((match = JSON_CONTAINER_MEMBER.exec(value)) !== null) {
    if (!isSensitiveKey(match[1]) || match.index < copiedTo) {
      continue;
    }
    const spanEnd = findSpanEnd(value, match.index + match[0].length - 1);
    if (spanEnd === -1) {
      // A cut-off transfer leaves partial JSON in response.data; the unclosable container is the one being redacted.
      return `${result}${value.slice(copiedTo, match.index)}"${match[1]}":"${REDACTED}"${TRUNCATED}`;
    }
    result += value.slice(copiedTo, match.index) + `"${match[1]}":"${REDACTED}"`;
    copiedTo = spanEnd + 1;
    JSON_CONTAINER_MEMBER.lastIndex = copiedTo;
  }
  return copiedTo === 0 ? value : result + value.slice(copiedTo);
};

const redactJSONScalars = value => value.replace(
  JSON_SCALAR_MEMBER,
  (member, name) => (isSensitiveKey(name) ? `"${name}":"${REDACTED}"` : member)
);

const redactEscapedJSONScalars = value => value.replace(
  JSON_ESCAPED_MEMBER,
  (member, name) => (isSensitiveKey(name) ? `\\"${name}\\":\\"${REDACTED}\\"` : member)
);

// $q hands $exceptionHandler a JSON.stringify of every unhandled rejection, with no keys left to walk.
export const redactString = value => redactEscapedJSONScalars(
  redactJSONScalars(redactJSONContainers(redactURL(value)))
);

// An accessor that throws must not cost the whole log entry.
const readProperty = (value, key) => {
  try {
    return { ok: true, value: value[key] };
  } catch (e) {
    return { ok: false };
  }
};

const readOwn = (value, key) => {
  const read = readProperty(value, key);
  return read.ok ? read.value : undefined;
};

const ownKeys = value => {
  try {
    return Object.keys(value);
  } catch (e) {
    return [];
  }
};

// Nothing enforces that `message` is a string, and a throwing toString() must not escape.
const coerceToRedactedString = value => {
  if (typeof value === 'string') {
    return redactString(value);
  }
  if (value === undefined || value === null) {
    return '';
  }
  try {
    return redactString(String(value));
  } catch (e) {
    return UNREADABLE;
  }
};

// `__proto__` arrives as a real own key out of JSON.parse, and `=` would hit the setter.
export const assign = (target, key, value) => {
  if (key === '__proto__') {
    Object.defineProperty(target, key, {
      value: value,
      enumerable: true,
      writable: true,
      configurable: true,
    });
    return;
  }
  target[key] = value;
};

// Returns the number of keys it did not copy.
const copyKeys = (source, target, keys, depth, context) => {
  for (let index = 0; index < keys.length; index++) {
    if (context.emitted >= MAX_EMITTED) {
      return keys.length - index;
    }
    context.emitted += 1;
    const key = keys[index];
    if (isSensitiveKey(key)) {
      assign(target, key, REDACTED);
    } else {
      const read = readProperty(source, key);
      assign(target, key, read.ok ? redactValue(read.value, depth + 1, context) : UNREADABLE);
    }
  }
  return 0;
};

// JSON.stringify drops a non-index property, so on an array the marker has to be an element.
const noteSkipped = (target, skipped) => {
  if (skipped <= 0) {
    return;
  }
  if (Array.isArray(target)) {
    target.push(`${TRUNCATED} ${skipped} more`);
    return;
  }
  assign(target, TRUNCATED, skipped);
};

const ERROR_OWN_SLOTS = ['message', 'name', 'stack'];

const redactError = (error, depth, context) => {
  const copy = new Error(coerceToRedactedString(readOwn(error, 'message')));
  const name = readOwn(error, 'name');
  if (name !== undefined) {
    // `name` lives on Error.prototype, so `=` would add an enumerable own key a plain Error lacks.
    Object.defineProperty(copy, 'name', {
      value: coerceToRedactedString(name),
      enumerable: false,
      writable: true,
      configurable: true,
    });
  }
  // Emptied, not deleted: new Error()'s stack points at this file, and New Relic groups by stack.
  const stack = readOwn(error, 'stack');
  copy.stack = typeof stack === 'string' ? redactString(stack) : '';

  // `cause` is non-enumerable, so Object.keys() does not see it.
  const cause = readOwn(error, 'cause');
  if (cause !== undefined) {
    copy.cause = redactValue(cause, depth + 1, context);
  }

  noteSkipped(copy, copyKeys(
    error,
    copy,
    ownKeys(error).filter(key => ERROR_OWN_SLOTS.indexOf(key) === -1 && key !== 'cause'),
    depth,
    context
  ));
  return copy;
};

const isIndexKey = key => /^\d+$/.test(key);

const redactValue = (value, depth, context) => {
  if (typeof value === 'string') {
    return redactString(value);
  }
  if (typeof value === 'function') {
    return FUNCTION_PLACEHOLDER;
  }
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (value instanceof Date) {
    // Copied because InternalAnalyticsPublishingService queues the result for seconds first.
    return new Date(value.getTime());
  }

  // Only an ancestor is a cycle: the recap diagnostics share elements between parallel arrays.
  if (context.inProgress.has(value)) {
    return CIRCULAR;
  }
  if (depth >= MAX_DEPTH || context.emitted >= MAX_EMITTED) {
    return TRUNCATED;
  }
  // A byte buffer would otherwise be expanded one numeric key at a time.
  if (typeof ArrayBuffer !== 'undefined' && ArrayBuffer.isView && ArrayBuffer.isView(value)) {
    const label = (value.constructor && value.constructor.name) || 'TypedArray';
    // isView() is true for DataView, which has byteLength and no length.
    const size = typeof value.length === 'number'
      ? `length=${value.length}`
      : `byteLength=${value.byteLength}`;
    return `[${label} ${size}]`;
  }
  context.inProgress.add(value);
  context.emitted += 1;

  let copy;
  if (value instanceof Error) {
    copy = redactError(value, depth, context);
  } else if (Array.isArray(value)) {
    // Not map(): it drops non-index properties, and a throwing accessor would cost the whole argument.
    copy = [];
    // Bounded before the list is built: 3M elements would allocate 3M index strings first.
    const affordable = Math.max(0, MAX_EMITTED - context.emitted);
    const indexCount = Math.min(value.length, affordable);
    const keys = [];
    for (let index = 0; index < indexCount; index++) {
      keys.push(String(index));
    }
    const skipped = copyKeys(
      value,
      copy,
      keys.concat(ownKeys(value).filter(key => !isIndexKey(key))),
      depth,
      context
    );
    noteSkipped(copy, skipped + (value.length - indexCount));
  } else {
    copy = {};
    noteSkipped(copy, copyKeys(value, copy, ownKeys(value), depth, context));
  }

  context.inProgress.delete(value);
  return copy;
};

export const redactSensitiveValues = value => {
  try {
    return redactValue(value, 0, {
      inProgress: new Set(),
      emitted: 0,
    });
  } catch (e) {
    // Never fall through to the raw value.
    return UNREDACTABLE;
  }
};

export default redactSensitiveValues;
